import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { sendCustomTrackingInfo } from '@panel/commons';
import Switch from '@panel/commons/lib/Switch';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import LockClosedOutline from '@panel/commons/assets/icons/LockClosedOutline.svg';
import TableComponent from '@panel/commons/lib/Table';
import Alert from '@panel/commons/lib/Alert/Alert';
import { CollaboratorsSelectors, CollaboratorsOperations } from '../../ducks/collaborators';
import {
  TableContainer,
  StyledAction,
  SwitchContainer,
  TooltipComponent,
  AlertContainer,
  FooterContainer
} from './styles';
import NameValue from './NameValue';

const Collaborators = ({
  device,
  postingId,
  setActiveLoader = () => {},
  setDisabledSkeleton = () => {},
  setFinishedAction = () => {},
  userInfo
}) => {
  const [columnsValues, setColumnsValues] = useState([]);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    collaboratorsInfo,
    hasCollaboratorsInfo,
    errorCollaborators,
    loadingCollaborators,
    updatedPermission,
    updatingPermissions,
    errorUpdatePermission,
    currentUser
  } = useSelector(CollaboratorsSelectors.getCollaboratorsState);
  const columnNames = [
    { accessor: 'name', label: t('panel.contactosPermisos.table.columnName.name') },
    { accessor: 'mail', label: t('panel.contactosPermisos.table.columnName.mail') },
    { accessor: 'permissions', label: t('panel.contactosPermisos.table.columnName.permissions') }
  ];

  const columnNamesMobile = [
    { accessor: 'name', label: t('panel.contactosPermisos.table.columnName.name') },
    { accessor: 'permissions', label: t('panel.contactosPermisos.table.columnName.permissions') }
  ];

  /**
   * La función arma el texto a mostrar en el Tooltip.
   * @param {Number} userTypeId
   * @param {Boolean} isResponsible
   * @returns {String}
   */
  const getTooltipText = (userTypeId, isResponsible) => {
    if (isResponsible)
      return (
        t(`panel.contactosPermisos.table.columnName.is_responsible.tooltip`) +
        t('panel.contactosPermisos.table.columnName.is_responsible.tooltip.resp')
      );
    return null;
  };

  /**
   * Dispara la accion que actualiza el permiso del usuario.
   * @param {Object} collaborator
   * @returns {Null}
   */
  const switchHandleOnChange = collaborator => {
    if (!collaborator.isResponsible) {
      dispatch(
        CollaboratorsOperations.updateUserPermissions(
          parseInt(postingId),
          collaborator.userId,
          !collaborator.permission,
          collaborator.name
        )
      );
      sendCustomTrackingInfo({
        event: 'select_content',
        content_group: 'Panel',
        content_type: 'Mis avisos',
        item_id: `${collaborator.permission ? 'Deshabilitar usuario' : 'Habilitar usuario'}`
      });
    }
  };

  /**
   *  La función setea los valores {columnsValues} que van a completar el tbody de la tabla (setColumnsValues())
   * @returns {Null}
   */
  const getValues = () => {
    setColumnsValues(
      collaboratorsInfo.map(collaborator => {
        return {
          name: (
            <NameValue
              name={collaborator.lastName ? `${collaborator.name} ${collaborator.lastName}` : `${collaborator.name}`}
              isResponsible={collaborator.isResponsible}
              wordingIsResponsible={t('panel.contactosPermisos.table.columnName.is_responsible')}
              device={device}
            />
          ),
          mail: collaborator.email,
          permissions: (
            <SwitchContainer device={device}>
              <Switch
                id={collaborator.userId}
                toggled={collaborator.permission}
                onChange={() => switchHandleOnChange(collaborator)}
                dataPanel='collaboratorsSwitch'
                disabled={collaborator.isResponsible}
                width={device !== 'desktop' ? '42%' : '30%'}
              />
              {collaborator.isResponsible && (
                <SimpleTooltip
                  isMobile={device !== 'desktop'}
                  position='top-center'
                  text={React.createElement(
                    'div',
                    {},
                    <TooltipComponent>
                      {getTooltipText(collaborator.userTypeId, collaborator.isResponsible)}
                    </TooltipComponent>
                  )}
                  dataPanel={`panel-tooltip-${collaborator.userId}`}
                  autoPlacements
                  allowPlacements='top-start'
                  offset='-5'
                >
                  <StyledAction icon={LockClosedOutline} data-panel={`icon-${collaborator.userId}`} device={device} />
                </SimpleTooltip>
              )}
            </SwitchContainer>
          )
        };
      })
    );
  };

  /**
   * La función evalua los actions y results para armar las traducciones de los snackbars
   * @param {string} action
   * @param {string} result
   * @param {string} name
   * @param {boolean} isResponsible
   * @returns Se retorna la traducción armada
   */
  const snackBarTextBuilder = (action, result, name, isResponsible) => {
    if (action === 'permissions' && result === 'ERROR') {
      return t(`panel.contactosPermisos.${action}.snackbar.${result}`, { userName: name });
    }
    return t(`panel.contactosPermisos.${action}.snackbar.${result}.${isResponsible ? 'toEnable' : 'toDisable'}`, {
      userName: name
    });
  };

  /**
   * La función ordena el listado de usuarios en primer lugar por Responsable y luego por tipo de usuario (Admin > Superusuario)
   * @param {Array} usersArray
   * @returns se retorna el array de usuarios ordenado
   */
  const sortCollaboratorsList = collaboratorsArray => {
    if (!collaboratorsArray) return [];
    collaboratorsArray.sort((a, b) => {
      if (a.isResponsible > b.isResponsible) return -1;
      if (a.isResponsible < b.isResponsible) return 1;
      return a.userTypeId - b.userTypeId;
    });
    return collaboratorsArray;
  };

  // Se realiza FETCH para armar la tabla la primera vez (!hasCollaboratorsInfo) y cada vez que se actualicen los permisos de los usuarios (updatedPermission):
  useEffect(() => {
    if ((!hasCollaboratorsInfo || updatedPermission) && !errorCollaborators) {
      dispatch(CollaboratorsOperations.getCollaboratorsInfo(postingId));
    }
    // Se odena el listado de usuarios, mostrando de primero el usuario responsable:
    sortCollaboratorsList(collaboratorsInfo);
    // Se setean los datos para armar la tabla:
    getValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorsInfo, updatedPermission]);

  // Efecto para mostrar el LOADER al cambiar permisos de los usuarios:
  useEffect(() => {
    if (updatingPermissions) {
      setActiveLoader(true);
      // Se realiza el siguiente set para evitar que se muestre el skeleton al cambiar los permisos:
      setDisabledSkeleton(true);
    } else {
      setActiveLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingPermissions, loadingCollaborators]);

  // Efecto para disparar SNACKBARS al cambiar los permisos:
  useEffect(() => {
    if (updatedPermission) {
      setFinishedAction({
        action: 'permissions',
        result: 'SUCCESS',
        component: snackBarTextBuilder('permissions', 'SUCCESS', currentUser.name, currentUser.isResponsible),
        keepOpen: false
      });
    }
    if (errorUpdatePermission) {
      setFinishedAction({
        action: 'permissions',
        result: 'ERROR',
        component: snackBarTextBuilder('permissions', 'ERROR', currentUser.name),
        keepOpen: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedPermission, errorUpdatePermission]);

  return (
    <>
      {userInfo && userInfo.isIntegrator && (
        <AlertContainer device={device}>
          <Alert
            message={t('panel.contactosPermisos.ContactData.isIntegrator.message')}
            dataPabel='ContactData'
            minHeight='46px'
            fontSize='14px'
            justifyContent='start'
            withoutBorder
          />
        </AlertContainer>
      )}
      {columnsValues && columnsValues.length > 0 && (
        <TableContainer device={device}>
          <TableComponent
            data-panel='contactosPermisos-collaboratorsTable'
            columnNames={device !== 'mobile' ? columnNames : columnNamesMobile}
            columnsValues={columnsValues}
            dataPanel='collaboratorsTable'
            device={device}
          />
        </TableContainer>
      )}
      <FooterContainer dataPanel='footerMobileHelp' device={device}>
        {t('panel.contactosPermisos.ContactData.footerMobileHelp')}
      </FooterContainer>
    </>
  );
};

Collaborators.propTypes = {
  device: PropTypes.string.isRequired,
  postingId: PropTypes.string.isRequired,
  userInfo: PropTypes.shape({ isIntegrator: PropTypes.bool }).isRequired
};

export default Collaborators;
